import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import imageCompression from 'browser-image-compression'
import ReceiptPreview from '@/components/transaction/receipt-preview'
import type { ParsedReceiptData, ParsedItem } from '@/components/transaction/receipt-preview'
import { useProfiles, useCategories, useSuggestions, useCreateTransaction, useTags, useCreateTag } from '@/lib/hooks'
import type { Category } from '@/lib/hooks'
import { useAuth } from '@/lib/auth-context'
import { todayString } from '@/lib/utils'
import { authFetch } from '@/lib/auth-fetch'

function resolveCategories(
  categories: Category[],
  suggestion: string | null
): string | null {
  if (!suggestion) return null
  const match = categories.find((c) => c.name === suggestion)
  return match?.id ?? null
}

export default function ReceiptPage() {
  const navigate = useNavigate()
  const location = useLocation()

  const { data: profiles = [] } = useProfiles()
  const { data: categories = [] } = useCategories()
  const { storeNames, itemNames } = useSuggestions()
  const { user: currentUser } = useAuth()
  const { data: tags = [] } = useTags()
  const createTagMutation = useCreateTag()
  const createTx = useCreateTransaction()

  const [receiptLoading, setReceiptLoading] = useState(false)
  const [parsedReceipt, setParsedReceipt] = useState<ParsedReceiptData | null>(null)

  // Get file passed from MainLayout via router state
  const file = (location.state as { file?: File } | null)?.file

  const processFile = useCallback(async (file: File) => {
    setReceiptLoading(true)
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 2,
        maxWidthOrHeight: 2400,
      })

      const formData = new FormData()
      formData.append('image', compressed)

      const res = await authFetch('/api/receipt/parse', {
        method: 'POST',
        body: formData,
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.error || 'レシートの解析に失敗しました')
      }

      const items: ParsedItem[] = (data.items || []).map(
        (item: { name: string; amount: number; category_suggestion: string | null }) => ({
          name: item.name,
          amount: item.amount,
          category_id: resolveCategories(categories, item.category_suggestion),
          category_suggestion: item.category_suggestion,
        })
      )

      setParsedReceipt({
        date: data.date || todayString(),
        store_name: data.store_name || '',
        items,
        total_amount: data.total_amount || 0,
        tax_amount: data.tax_amount ?? 0,
      })
    } catch (e) {
      console.error('レシート解析に失敗:', e)
      toast.error(e instanceof Error ? e.message : 'レシートの解析に失敗しました')
      navigate('/', { replace: true })
    } finally {
      setReceiptLoading(false)
    }
  }, [categories, navigate])

  // Process the file when the page mounts with a file in state
  useEffect(() => {
    if (file) {
      processFile(file)
    } else {
      // No file provided — go back
      navigate('/', { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleReceiptSave = async (data: {
    date: string
    store_name: string
    memo?: string
    items: { name: string; amount: number; category_id: string | null }[]
    total_amount: number
    tax_amount: number
    paid_by: string
    input_method: string
    scope: 'household' | 'personal'
    tag_ids?: string[]
  }) => {
    await createTx.mutateAsync(data)
    navigate('/', { replace: true })
  }

  const handleReceiptCancel = () => {
    navigate('/', { replace: true })
  }

  if (receiptLoading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-4">
        <Loader2 className="size-10 animate-spin text-primary" />
        <p className="text-muted-foreground">レシートを解析中...</p>
      </div>
    )
  }

  if (parsedReceipt && currentUser) {
    return (
      <div className="mx-auto max-w-lg space-y-4 p-4">
        <ReceiptPreview
          parsedData={parsedReceipt}
          categories={categories}
          profiles={profiles}
          currentUserId={currentUser.id}
          tags={tags}
          storeNameSuggestions={storeNames}
          itemNameSuggestions={itemNames}
          onSave={handleReceiptSave}
          onCancel={handleReceiptCancel}
          onCreateTag={(data) => createTagMutation.mutate(data)}
        />
      </div>
    )
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-4">
      <Loader2 className="size-10 animate-spin text-muted-foreground" />
    </div>
  )
}
